import { type PageModule, PageModuleType } from '@/types/module.types';
import type { BrandliveEvent } from '@/types/projects.types';
import { EPaletteModes } from '@/types/theme-packs.types';
import type { LanguagesAbbr } from '@/types/translation.types';
import chroma from 'chroma-js';

export type ParseColorOptions = {
  opacity?: number;
};
export function parseColor([hex, opacity]: [string, number], opts?: ParseColorOptions): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const color = `rgba(${r},${g},${b},${opts?.opacity ?? opacity})`;
  return color;
}

export function resolveColor(themeColor: [string, number], customHex?: string): string {
  if (customHex) return customHex;
  return parseColor(themeColor);
}

function getMainEventBanner(module: PageModule) {
  return module.type === PageModuleType.main_event_banner;
}

function getBanner(eventBundle: BrandliveEvent) {
  if (eventBundle.settings.display_landing_page) {
    const mainBanner = eventBundle?.homepage?.modules.find(getMainEventBanner);
    if (mainBanner && mainBanner.is_on) return mainBanner;
  }

  if (eventBundle.settings.display_homepage) {
    const mainHomeBanner = eventBundle.homepage?.post_register_home_modules?.find(getMainEventBanner);
    if (mainHomeBanner?.is_on) {
      return mainHomeBanner;
    }
  }
}

export function getBannerImage(eventBundle: BrandliveEvent): string {
  if (eventBundle.registration_settings?.image) {
    return eventBundle.registration_settings.image;
  }

  if (eventBundle.event_image) {
    return eventBundle.event_image;
  }

  const banner = getBanner(eventBundle);

  if (banner) {
    return banner.content.image;
  } else {
    const session = eventBundle.sessions[0];
    // const templateName = eventBundle.template.name;
    return session.image || session.pre_broadcast_thumbnail || ``;
  }
}

export const titleToLines = (title: string): string => {
  const words = title.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if (!currentLine) {
      currentLine = word;
    } else if ((currentLine + ' ' + word).length <= 12) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) lines.push(currentLine);

  return lines.join('<br/>');
};

export const btvFallbacks = {
  [EPaletteModes.Light]: 'https://assets.brandlive.com/assets/bl-platform/images/brandtv/btv-black.svg',
  [EPaletteModes.Dark]: 'https://assets.brandlive.com/assets/bl-platform/images/brandtv/btv-white.svg',
};

export function getBtvFallbackForBg(bgColor: string): string {
  try {
    if (!chroma.valid(bgColor)) return btvFallbacks[EPaletteModes.Dark];
    return chroma(bgColor).luminance() > 0.5 ? btvFallbacks[EPaletteModes.Light] : btvFallbacks[EPaletteModes.Dark];
  } catch {
    return btvFallbacks[EPaletteModes.Dark];
  }
}

export function getHeaderImage(eventBundle: BrandliveEvent, fallback?: string | null): string {
  if (eventBundle.registration_settings?.logo) {
    return eventBundle.registration_settings.logo;
  }

  return fallback || '';
}

// Rough estimate: average character is ~0.5-0.6 times the font size
const charWidth = (fontSize = 16) => fontSize * 0.55;

const parse = (text: string) => {
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.innerText;
};

export const description = (
  event: BrandliveEvent,
  language: LanguagesAbbr,
  maxWidth: number,
  maxLines = 3,
  fontSize = 16
) => {
  if (event.event_description) return event.event_description;

  const banner = getBanner(event);
  const reg = event.registration_settings?.description?.[language ?? 'base'];
  const sDesc = event.sessions[0]?.description?.[language ?? 'base'];
  const text = parse(banner?.content?.description?.[language ?? 'base'] ?? reg ?? sDesc);
  const maxLen = (maxWidth * maxLines) / charWidth(fontSize);
  if (text.length > maxLen) {
    return text.substring(0, text.indexOf(' ', maxLen)) + '...';
  }

  return text;
};

export const eventImage = (event: BrandliveEvent) => {
  return getBannerImage(event);
};

export const formatTimestamp = (ts: number): string =>
  new Date(ts).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });

export const formatDateParts = (ts: number) => {
  const date = new Date(ts);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const hour12 = hours % 12 || 12;
  return {
    dayName: date.toLocaleString('en-US', { weekday: 'long' }).toUpperCase(),
    shortDate: date.toLocaleString('en-US', { month: 'short', day: 'numeric' }).toUpperCase(),
    numericDate: `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`,
    period: hours >= 12 ? 'PM' : 'AM',
    time: `${hour12}:${minutes}`,
  };
};
